import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import useFirestore from "hooks/useFirestore";
import type { DisplayUserNote } from "hooks/useListingNotes";
import {
  USER_NOTES_COLLECTION_NAME,
  UserNote,
  UserNotesDocument
} from "../../../@types/sharedSchema";
import { applyNoteShare, withDefaultNoteShare } from "utils/noteSharing";

type UseSellerNotesReturn = {
  notes: DisplayUserNote[];
  hasNotes: boolean | undefined;
  isLoading: boolean;
  addNote: (text: string) => void;
  updateNote: (noteId: string, text: string) => void;
  deleteNote: (noteId: string) => void;
  setNoteOrgShare: (noteId: string, share: boolean) => void;
};

// See matching docs in useListingNotes — keep the field absent (not `[]`)
// whenever no note is org-shared, so the array-contains query and
// firestore.rules check both work without filtering empties.
function computeDocOrgIds(notes: UserNote[]): string[] | undefined {
  const set = new Set<string>();
  for (const n of notes) {
    for (const orgId of n.organizationIds ?? []) set.add(orgId);
  }
  return set.size > 0 ? Array.from(set) : undefined;
}

/**
 * Real-time notes for a contact, including teammates' org-shared notes.
 * Mirrors useListingNotes: a primary listener for the current user's own
 * doc and a second listener for other users' docs that have at least one
 * note shared with the current user's organization for this contact.
 *
 * Mutators (`addNote` / `updateNote` / `deleteNote` / `setNoteOrgShare`)
 * only operate on the user's own notes. Teammates' notes appear in the
 * popover read-only with author attribution.
 */
export function useSellerNotes(
  contactId: string | undefined,
  enabled: boolean
): UseSellerNotesReturn {
  const {
    user: { email: userEmail }
  } = useAuth();
  const { organizationId, organizations, organizationUsers } = useFirestore();

  const [myNotes, setMyNotes] = useState<UserNote[]>([]);
  // Other users' notes (filtered to those shared with my org) for the same
  // contact. See useListingNotes for the matching listings flow.
  const [orgScopedNotes, setOrgScopedNotes] = useState<DisplayUserNote[]>([]);
  const [hasNotes, setHasNotes] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const docIdRef = useRef<string | null>(null);

  // Listener 1 — the current user's own notes doc for this contact.
  // Stays as a `userId + contactId` query because contact-scoped docs use
  // auto-generated ids (the listings flow uses a deterministic doc id and
  // can `.doc(docId)` directly — see useListingNotes).
  useEffect(() => {
    if (!contactId || !enabled || !userEmail) {
      return;
    }

    /**
     * ⚠️ CLEAR THE PREVIOUS CONTACT'S NOTES BEFORE LISTENING FOR THIS ONE.
     *
     * Nothing else resets these: the snapshot handler below overwrites `myNotes` when it
     * arrives, listener 2 does the same for `orgScopedNotes`, and `docIdRef` is written
     * only from inside a snapshot. So a hook instance whose `contactId` CHANGES holds the
     * old contact's notes for the whole round trip — and `docIdRef` pointing at the old
     * document is worse than a display bug: `updateNote`/`deleteNote` write through it.
     *
     * `setIsLoading(true)` was masking the display half of that and nothing more, which is
     * exactly why it must not be "optimised away" to suppress a spinner: it is the only
     * thing standing between a re-keyed row and someone else's notes.
     *
     * Not hypothetical — `CrmEditContactDialog`'s phone change RE-KEYS a contact in place
     * (the doc id IS the phone), and the CRM list merges the new id onto the same row
     * rather than remounting it, so this instance's `contactId` prop genuinely changes
     * under it.
     */
    setMyNotes([]);
    setOrgScopedNotes([]);
    setHasNotes(undefined);
    docIdRef.current = null;
    setIsLoading(true);

    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("userId", "==", userEmail)
      .where("contactId", "==", contactId)
      .limit(1)
      .onSnapshot(
        (snapshot) => {
          if (!snapshot.empty) {
            const doc = snapshot.docs[0];
            const data = doc.data() as UserNotesDocument;
            const parsed = (data.notes || []).map((n) => ({
              ...n,
              noteTime: (n.noteTime as any)?.toDate?.() ?? new Date(n.noteTime)
            }));
            docIdRef.current = doc.id;
            setMyNotes(parsed);
          } else {
            docIdRef.current = null;
            setMyNotes([]);
          }
          setIsLoading(false);
        },
        (error) => {
          console.error("Failed to listen to notes:", error);
          setIsLoading(false);
        }
      );

    return () => {
      unsubscribe();
    };
  }, [enabled, contactId, userEmail]);

  // Listener 2 — teammates' docs that share notes for this contact with my
  // org. Per-doc, we further filter the inner `notes` array to only those
  // marked shared with my org (the doc-level `organizationIds` is the
  // union, so it may legitimately include docs with mixed shared+private).
  useEffect(() => {
    if (!contactId || !enabled || !organizationId || !userEmail) return;

    const unsubscribe = db
      .collection(USER_NOTES_COLLECTION_NAME)
      .where("contactId", "==", contactId)
      .where("organizationIds", "array-contains", organizationId)
      .onSnapshot(
        (snapshot) => {
          const collected: DisplayUserNote[] = [];
          snapshot.docs.forEach((d) => {
            const data = d.data() as UserNotesDocument;
            // Skip own doc — listener 1 already handles it.
            if (data.userId === userEmail) return;
            for (const n of data.notes ?? []) {
              if (!(n.organizationIds ?? []).includes(organizationId)) continue;
              const noteTime = (n.noteTime as any)?.toDate?.() ?? new Date(n.noteTime);
              collected.push({
                ...n,
                noteTime,
                isMine: false,
                authorEmail: data.userId
              });
            }
          });
          setOrgScopedNotes(collected);
        },
        (error) => {
          console.error("useSellerNotes org-notes error", error);
        }
      );

    return () => unsubscribe();
  }, [enabled, contactId, organizationId, userEmail]);

  // Merge mine + teammates' shared, sort by recency, resolve author names.
  const notes = useMemo<DisplayUserNote[]>(() => {
    const nameByEmail = new Map<string, string>();
    for (const u of organizationUsers ?? []) {
      if (u.email) nameByEmail.set(u.email, u.name || u.email);
    }
    const mineWithMeta: DisplayUserNote[] = myNotes.map((n) => ({
      ...n,
      isMine: true,
      authorEmail: userEmail ?? ""
    }));
    const merged = [...mineWithMeta, ...orgScopedNotes].sort(
      (a, b) => (b.noteTime?.getTime() ?? 0) - (a.noteTime?.getTime() ?? 0)
    );
    return merged.map((n) =>
      n.isMine
        ? n
        : { ...n, authorName: nameByEmail.get(n.authorEmail) ?? n.authorEmail }
    );
  }, [myNotes, orgScopedNotes, organizationUsers, userEmail]);

  useEffect(() => {
    setHasNotes(notes.length > 0);
  }, [notes.length]);

  const saveNotes = useCallback(
    (updatedNotes: UserNote[]) => {
      if (!contactId || !userEmail) return;

      const docOrgIds = computeDocOrgIds(updatedNotes);
      const docData: UserNotesDocument = {
        userId: userEmail,
        contactId,
        notes: updatedNotes,
        // Conditional include — omitting the field when no note is shared
        // makes set() (no merge) drop it from the doc.
        ...(docOrgIds ? { organizationIds: docOrgIds } : {})
      };

      if (docIdRef.current) {
        db.collection(USER_NOTES_COLLECTION_NAME)
          .doc(docIdRef.current)
          .set(docData)
          .catch((err) => {
            console.error("Failed to save notes:", err);
          });
      } else {
        db.collection(USER_NOTES_COLLECTION_NAME)
          .add(docData)
          .then((ref) => {
            docIdRef.current = ref.id;
          })
          .catch((err) => {
            console.error("Failed to save notes:", err);
          });
      }
    },
    [contactId, userEmail]
  );

  // Mutators operate on `myNotes`, NOT the merged `notes` (which contains
  // teammates' notes). Rules also deny writes to others' docs from us.
  const addNote = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      // Shared with the author's offices by default (`utils/noteSharing.ts`).
      const newNote = withDefaultNoteShare(
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          note: text.trim(),
          noteTime: new Date()
        },
        organizations
      );
      saveNotes([newNote, ...myNotes]);
    },
    [myNotes, organizations, saveNotes]
  );

  const updateNote = useCallback(
    (noteId: string, text: string) => {
      if (!myNotes.some((n) => n.id === noteId)) return;
      const updated = myNotes.map((n) =>
        n.id === noteId ? { ...n, note: text.trim(), noteTime: new Date() } : n
      );
      saveNotes(updated);
    },
    [myNotes, saveNotes]
  );

  const deleteNote = useCallback(
    (noteId: string) => {
      if (!myNotes.some((n) => n.id === noteId)) return;
      saveNotes(myNotes.filter((n) => n.id !== noteId));
    },
    [myNotes, saveNotes]
  );

  // Toggle whether one of the user's OWN notes is shared with their org.
  // No-op for teammates' notes; rules would deny the write anyway.
  // Shared = every office the author is in; private = nobody, the field
  // dropped entirely — see `utils/noteSharing.ts`.
  const setNoteOrgShare = useCallback(
    (noteId: string, share: boolean) => {
      if (!organizationId) return;
      if (!myNotes.some((n) => n.id === noteId)) return;
      saveNotes(myNotes.map((n) => (n.id === noteId ? applyNoteShare(n, share, organizations) : n)));
    },
    [myNotes, organizationId, organizations, saveNotes]
  );

  return { notes, hasNotes, isLoading, addNote, updateNote, deleteNote, setNoteOrgShare };
}
